import { FC, useCallback, useMemo, useState } from 'react'
import { LastLayerContext } from '@cargomart/ui'
import { useNavigate } from '@tanstack/react-router'
import { MODAL_IDS, Seo, useModal } from 'components'
import Mobile from 'components/ModalForm/Mobile'
import { checkIsLastLayer } from 'components/ModalItem/helpers'
import { Form } from 'forms/Form'
import { useIsMobile } from 'hooks'
import { authRoute } from 'modules/login/routes'
import { UserDocumentTypeEnum } from 'modules/profile/types'
import { changeFields } from 'shared/forms/mutators'
import { useLazyOrderYarcheAuthInitQuery } from 'src/modules/login/api'

import { OrderCreateAgreeOffer } from '../../components/OrderCreate/OrderCreateAgreeOffer'
import OrderYarcheGuestCreateForm from '../../components/OrderYarcheGuestCreateForm'
import OrderYarcheGuestCreatePageDesktop from '../../components/OrderYarcheGuestCreatePageDesktop'
import { OrderYarcheGuestCreateFormData } from './types'

export const OrderYarcheGuestCreatePage: FC = () => {
  const [isShowPhone, setIsShowPhone] = useState(false)
  const isMobile = useIsMobile()
  const { modals } = useModal()
  const [orderYarcheAuthInit, { isLoading: isLoadingOrderYarcheGuestCreate }] = useLazyOrderYarcheAuthInitQuery()
  const navigate = useNavigate()
  const initialValues = useMemo(
    () => ({
      cargo: {
        sizeId: 'envelope'
      },
      departurePartner: {
        document: {
          type: UserDocumentTypeEnum.PassportRF
        }
      }
    }),

    []
  )

  const handleClose = useCallback(() => {
    navigate(authRoute)
  }, [navigate])

  const handleSuccess = useCallback((widgetUrl: string) => {
    if (widgetUrl) {
      window.location.replace(widgetUrl)
    }

    return undefined
  }, [])

  const handleSubmit = useCallback(
    async (data: OrderYarcheGuestCreateFormData) => {
      if (!isShowPhone) {
        setIsShowPhone(true)
      } else if (data?.username) {
        const { username } = data
        sessionStorage.setItem('orderYarcheFormData', JSON.stringify(data))

        return orderYarcheAuthInit({ username }).unwrap().then(handleSuccess)
      }
    },
    [handleSuccess, isShowPhone, orderYarcheAuthInit]
  )

  const formView = useMemo(() => <OrderYarcheGuestCreateForm isShowPhone={isShowPhone} />, [isShowPhone])

  return (
    <>
      <Seo
        description="Оформление новой посылки для размещения в сервисе доставки сборных грузов QP5 Express. Заполнение данных о грузе и предварительный расчет стоимости грузоперевозки."
        title="Новая посылка – сборные перевозки QP5 Express"
      />

      <LastLayerContext.Provider value={checkIsLastLayer(MODAL_IDS.OrderYarcheGuestCreate, modals)}>
        <Form<OrderYarcheGuestCreateFormData>
          keepDirtyOnReinitialize
          initialValues={initialValues}
          mutators={{ changeFields }}
          name="createOrderYarcheGuestForm"
          onSubmit={handleSubmit}
          render={(formProps) => (
            <>
              <form
                className="max-w-[990px] rounded-none bg-surface pb-5 shadow-1 3md:rounded"
                name="createOrderYarcheGuestForm"
                onSubmit={formProps.handleSubmit}
              >
                {isMobile ? (
                  <Mobile
                    buttonLabel="Заказать"
                    footerBefore={<OrderCreateAgreeOffer className="mb-4" />}
                    handleCloseWithConfirm={handleClose}
                    id="createOrderYarcheGuestFormMobile"
                    isLoading={isLoadingOrderYarcheGuestCreate}
                    title="Оформление посылки"
                  >
                    {formView}
                  </Mobile>
                ) : (
                  <OrderYarcheGuestCreatePageDesktop
                    handleCloseWithConfirm={handleClose}
                    id="createOrderYarcheGuestFormDesktop"
                    isLoading={isLoadingOrderYarcheGuestCreate}
                    title="Оформление посылки"
                  >
                    {formView}
                  </OrderYarcheGuestCreatePageDesktop>
                )}
              </form>
            </>
          )}
        />
      </LastLayerContext.Provider>
    </>
  )
}
